import {Component, EventEmitter, Input, Output, SimpleChanges} from '@angular/core';
import { DashboardType } from '@app/core/enums/dashboard-types.enum';
import { GenericDashboardComponent } from '@app/shared/components/generic-dashboard/generic-dashboard.component';
import { ReferralDashboardService } from '@app/features/referralDashboard/services/referral-dashboard.service';
import { AppointmentSearchParams } from '@app/shared/interfaces/appointmentSearchParams.interface';
import {Observable, Subject} from 'rxjs';
import { NgAppointments } from '@app/shared/interfaces/appointment.interface';
import {DashboardHeaderComponent} from "@app/layout/components/dashboard-header/dashboard-header.component";
import {LoadSpinnerWrapperComponent} from "@app/shared/components/load-spinner-wrapper/load-spinner-wrapper.component";
import {MatTab, MatTabChangeEvent, MatTabGroup} from "@angular/material/tabs";
import { NgComponentOutlet, NgTemplateOutlet } from "@angular/common";
import {SearchComponent} from "@app/shared/components/search/search.component";
import {AppointmentListComponent} from "@app/shared/components/appointment-list/appointment-list.component";
import {ContactUsInfoComponent} from "@app/shared/components/contact-us-info/contact-us-info.component";

import {
  appointmentType,
  GenericDashboardTabConfig
} from "@app/shared/components/referral-dashboard2/generic-dashboard.component";
import {ReferralAppointmentListComponent} from "@app/shared/components/appointment-list/referral-appointment-list.component";
type DefaultDashboardTabKey = 'upcoming' | 'previous' | 'search';

@Component({
  selector: 'app-referral-dashbboard',
  imports: [
    DashboardHeaderComponent,
    LoadSpinnerWrapperComponent,
    MatTab,
    MatTabGroup,
    NgComponentOutlet,
    NgTemplateOutlet,
    SearchComponent
],
  templateUrl: './referral-dashbboard.component.html',
  styleUrl: './referral-dashbboard.component.scss'
})
export class ReferralDashbboardComponent {
  dashboardType = DashboardType.Referral;
  appointmentListInputs = { showReferringProvider: true };
  patientSearchInputs = { dashboardType: this.dashboardType };
  contactInfoInputs = { dashboardType: this.dashboardType };

  constructor(
    private referralDashboardService: ReferralDashboardService,
  ) {}
  fetchAppointments$: (filter: AppointmentSearchParams) => Observable<NgAppointments> = (filter) => this.referralDashboardService.getReferralUserAppointments$(filter);
  fetchAppointmentDetails$: (ids: string[]) => Observable<NgAppointments> = (ids) => this.referralDashboardService.getReferralUserAppointmentDetails$(ids);

  @Input() showHeader = true;
  @Input() useLoadingWrapper = false;
  @Input() loading = false;
  @Input() appointmentContainerClass = 'appointment-list-container overflow-x-auto w-[99%] mt-4 mb-4 border-2 p-4 ml-auto mr-auto rounded-lg bg-white';

  @Input() customTabConfigs: GenericDashboardTabConfig[] = [];
  @Input() selectedIndex = 0;
  @Input() defaultSearchPlaceholder = 'Search by Patient Name, DOB, Visit, Location, or Provider';

  @Output() selectedIndexChange = new EventEmitter<number>();

  pageTitle: string = 'referral partner portal';

  tabConfigs: GenericDashboardTabConfig[] = [];

  readonly appointmentListComponent = ReferralAppointmentListComponent;
  readonly contactInfoComponent = ContactUsInfoComponent;


  private readonly searchTerms: Record<DefaultDashboardTabKey, string> = {
    upcoming: '',
    previous: '',
    search: ''
  };


  ngOnInit(): void {
    this.syncTabConfigs();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['customTabConfigs'] ||
      changes['dashboardType'] ||
      changes['appointmentListInputs'] ||
      changes['patientSearchInputs'] ||
      changes['contactInfoInputs']
    ) {
      this.syncTabConfigs();
    }
  }

  trackTab(index: number, tab: GenericDashboardTabConfig): string {
    return tab.key;
  }

  onTabChange(event: MatTabChangeEvent): void {
    this.selectedIndex = event.index;
    this.selectedIndexChange.emit(event.index);
  }

  onTabSearch(tabKey: string, searchTerm: string): void {
    if (!this.isDefaultTabKey(tabKey)) {
      return;
    }
    const normalizedSearchTerm = this.normalizeSearchTerm(searchTerm);

    this.searchTerms[tabKey] = normalizedSearchTerm;
    this.syncTabConfigs();
  }

  // This dashboard used to handle populating appointments and passed the data source to the list/search component.
  // Due to pagination, the loading the data sources were moved to the child components. We may want to move pagination to
  // this component in the future to be able to load data sources here.
  shouldRenderTabContent(tab: GenericDashboardTabConfig, tabIndex: number): boolean {
    return !tab.renderWhenActiveOnly || this.selectedIndex === tabIndex;
  }

  private syncTabConfigs(): void {
    if (this.customTabConfigs.length > 0) {
      this.tabConfigs = this.customTabConfigs;
      return;
    }

    const upcomingAppointmentInputs = {
      ...this.appointmentListInputs,
      title: 'Upcoming Appointments',
      dashboardType: this.dashboardType,
      appointmentListType: appointmentType.FutureAppointments,
      defaultSortField: 'appointmentDate',
      defaultSortDirection: 'asc',
      fetchAppointments$: this.fetchAppointments$,
      fetchAppointmentDetails$: this.fetchAppointmentDetails$,
      search: this.searchTerms['upcoming']
    };

    const previousAppointmentInputs = {
      ...this.appointmentListInputs,
      title: 'Previous Appointments',
      dashboardType: this.dashboardType,
      showStatus: true,
      appointmentListType: appointmentType.PastAppointments,
      fetchAppointments$: this.fetchAppointments$,
      fetchAppointmentDetails$: this.fetchAppointmentDetails$,
      defaultSortField: 'appointmentDate',
      defaultSortDirection: 'desc',
      search: this.searchTerms['previous']
    };

    const patientSearchTabInputs = {
      ...this.patientSearchInputs,
      title: 'Patient Search',
      dashboardType: this.dashboardType,
      showStatus: true,
      appointmentListType: appointmentType.PatientSearch,
      fetchAppointments$: this.fetchAppointments$,
      fetchAppointmentDetails$: this.fetchAppointmentDetails$,
      showReferringProvider: true,
      search: this.searchTerms['search']
    };

    this.tabConfigs = [
      {
        key: 'upcoming',
        label: 'Upcoming Appointments',
        component: this.appointmentListComponent,
        inputs: upcomingAppointmentInputs,
        showSearch: true,
        searchPlaceholder: this.defaultSearchPlaceholder,
        containerClass: this.appointmentContainerClass,
        footerComponent: this.contactInfoComponent,
        footerInputs: this.contactInfoInputs
      },
      {
        key: 'previous',
        label: 'Previous Appointments',
        component: this.appointmentListComponent,
        inputs: previousAppointmentInputs,
        showSearch: true,
        searchPlaceholder: this.defaultSearchPlaceholder,
        containerClass: this.appointmentContainerClass,
        footerComponent: this.contactInfoComponent,
        footerInputs: this.contactInfoInputs
      },
    ];

    this.tabConfigs.push({
      key: 'search',
      label: 'Patient Search',
      component: this.appointmentListComponent,
      inputs: patientSearchTabInputs,
      showSearch: true,
      searchPlaceholder: this.defaultSearchPlaceholder,
      containerClass: this.appointmentContainerClass,
      footerComponent: this.contactInfoComponent,
      footerInputs: this.contactInfoInputs
    })
  }

  private isDefaultTabKey(tabKey: string): tabKey is DefaultDashboardTabKey {
    return tabKey === 'upcoming' || tabKey === 'previous' || tabKey === 'search';
  }

  private normalizeSearchTerm(searchTerm: string): string {
    return (searchTerm ?? '').trim().replace(/\s+/g, ' ');
  }
}
